import { useState } from "react";

import { Dialog } from "../../components/Dialog";
import { Badge, Button } from "../../components/ui";
import { formatBytes } from "../../lib/format";
import type { OrderFile } from "../orders/types";

/** File types the quick viewer can show (others are download-only). */
const VIEWABLE = new Set(["pdf", "png", "jpeg"]);

/** An order's attachments with Download and VIEW (quick preview popup) links. */
export function OrderFileList({ orderId, files }: { orderId: string; files: OrderFile[] }) {
  const [viewing, setViewing] = useState<OrderFile | null>(null);
  const base = `/api/v1/staff/orders/${orderId}/files`;
  if (files.length === 0) return <p className="text-sm text-ink-muted">No files attached.</p>;
  return (
    <>
      <ul className="divide-y divide-line" aria-label="Attached files">
        {files.map((f) => (
          <li
            key={f.id}
            className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-1.5"
          >
            <span className="min-w-0 break-words text-sm">
              {f.name} <span className="text-ink-muted">({formatBytes(f.size_bytes)})</span>
            </span>
            {f.available ? (
              <span className="flex items-center gap-4 text-sm font-semibold print:hidden">
                {VIEWABLE.has(f.detected_type) && (
                  <button
                    type="button"
                    className="text-brand-700 underline"
                    aria-label={`View ${f.name}`}
                    onClick={() => setViewing(f)}
                  >
                    VIEW
                  </button>
                )}
                <a
                  className="text-brand-700 underline"
                  href={`${base}/${f.id}/download`}
                  aria-label={`Download ${f.name}`}
                >
                  Download
                </a>
              </span>
            ) : (
              <Badge>Removed</Badge>
            )}
          </li>
        ))}
      </ul>
      {viewing && (
        <FileViewer url={`${base}/${viewing.id}`} file={viewing} onClose={() => setViewing(null)} />
      )}
    </>
  );
}

function FileViewer({ url, file, onClose }: { url: string; file: OrderFile; onClose: () => void }) {
  const view = `${url}/view`;
  return (
    <Dialog
      open
      wide
      title={file.name}
      onClose={onClose}
      footer={
        <>
          <a
            className="inline-flex min-h-11 items-center px-3 font-semibold text-brand-700 underline"
            href={view}
            target="_blank"
            rel="noreferrer"
          >
            Open in a new tab
          </a>
          <a
            className="inline-flex min-h-11 items-center px-3 font-semibold text-brand-700 underline"
            href={`${url}/download`}
          >
            Download
          </a>
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      {file.detected_type === "pdf" ? (
        <iframe
          title={`Preview of ${file.name}`}
          src={view}
          className="h-[70vh] w-full rounded border border-line"
        />
      ) : (
        <img
          src={view}
          alt={`Preview of ${file.name}`}
          className="mx-auto max-h-[70vh] w-auto max-w-full object-contain"
        />
      )}
    </Dialog>
  );
}
